import { map, orderBy, isEmpty } from 'lodash';
import { getSaleAmount } from 'utils/general';
import { formatDataValue, openPdfInNewTab, renderBase64PdfLink } from 'utils/formatFns';
import { isTMSDistributorFn, isMexicanDistributorFn } from 'utils/sectionMappingUtils';

const distributorDealerSalesMapper = ({
  documents,
  distributorSales,
  distributorInvoiceLoading,
  isMexicanDistributor,
}) => {
  const withInvoiceDate = orderBy(
    map(distributorSales, (distributorSale) => ({
      ...distributorSale,
      invoiceDate: formatDataValue({
        value: distributorSale?.creditDateFmt || distributorSale?.invoiceDateFmt,
      }),
      invoiceTimestamp: formatDataValue({
        value: distributorSale?.creditDate || distributorSale?.invoiceDate,
      }),
    })),
    ['invoiceTimestamp', 'saleSequenceNumber'],
    ['desc', 'desc'],
  );

  return map(withInvoiceDate, (distributorSale, index) => {
    const invoice = documents?.distributorInvoices?.find(
      (invoice) =>
        invoice.invoiceNumber ===
        (distributorSale.invoiceNumber || distributorSale.creditInvoiceNumber),
    );

    return {
      id: `${index + 1}`,
      invoiceDate: distributorSale?.invoiceDate,
      saleSequenceNumber: distributorSale?.saleSequenceNumber,
      transactionNo: invoice?.hasPermissions
        ? renderBase64PdfLink({
            name: distributorSale?.creditInvoiceNumber || distributorSale?.invoiceNumber,
            isHeroAttribute: false,
            handleClick: () => openPdfInNewTab(invoice.base64),
            loading: distributorInvoiceLoading,
            label: `distributor-transaction-no-${index}`,
          })
        : formatDataValue({
            value: distributorSale?.creditInvoiceNumber || distributorSale?.invoiceNumber,
          }),
      amount: getSaleAmount(
        distributorSale?.creditAmount || distributorSale?.invoiceAmount,
        distributorSale?.creditIndicator,
        isMexicanDistributor,
      ),
      region: `${formatDataValue({ value: distributorSale?.saleRegionCode })} | ${formatDataValue({
        value: distributorSale?.saleRegionName,
      })}`,
      netSales: distributorSale?.creditIndicator ? '-1' : '+1',
      isCredit: distributorSale?.creditIndicator,
      sequence: index + 1,
      status: index === 0 ? 'current' : null,
      loading: distributorInvoiceLoading,
      businessSaleMonth: formatDataValue({
        value: distributorSale?.creditDateFmt
          ? distributorSale?.creditBusinessSalesMonthFmt
          : distributorSale?.invoiceBusinessSalesMonthFmt,
      }),
      issueDate: formatDataValue({
        value: distributorSale?.creditDateFmt || distributorSale?.invoiceDateFmt,
      }),
      draftDate: formatDataValue({
        value: distributorSale?.draftDateFmt,
      }),
      actualReleaseDate: formatDataValue({
        value: distributorSale?.actualReleaseDateFmt,
      }),
    };
  });
};

const transactionDetailsMapper = (distributorSales) =>
  orderBy(
    map(distributorSales, (distributorSale) => ({
      issueDate: formatDataValue({
        value: distributorSale?.creditDateFmt || distributorSale?.invoiceDateFmt,
      }),
      issueTimestamp: distributorSale?.creditDate || distributorSale?.invoiceDate,
      draftNumber: formatDataValue({
        value: distributorSale?.draftNumber,
      }),
      draftDate: formatDataValue({
        value: distributorSale?.draftDateFmt,
      }),
      checkNumber: formatDataValue({
        value: distributorSale?.checkNumber,
      }),
      bankCode: formatDataValue({
        value: distributorSale?.bankCode,
      }),
      actualReleaseDate: formatDataValue({
        value: distributorSale?.actualReleaseDateFmt,
      }),
    })),
    ['issueTimestamp'],
    ['desc'],
  );

export const getLatestSalesRegionName = ({ distributorDealerSales }) => {
  if (isEmpty(distributorDealerSales)) {
    return null;
  }
  let latestDistributorDealerSale = distributorDealerSales[0];
  distributorDealerSales?.forEach((sale) => {
    if (latestDistributorDealerSale?.srcCreateTimestamp < sale?.srcCreateTimestamp) {
      latestDistributorDealerSale = sale;
    }
  });
  return latestDistributorDealerSale?.saleRegionName || 'N/A';
};

export const distributorSale = ({
  vehicle,
  documents,
  roles,
  userRegion,
  distributorInvoiceLoading,
  isVehicleWithLimitedFeatures,
  features,
}) => {
  let distributorSaleVehicle = isVehicleWithLimitedFeatures
    ? vehicle?.salesInfo?.distributorDealerSales?.filter(
        (sale) => sale?.saleType === 'DistributorSale',
      ) || []
    : vehicle?.salesInfo?.privateDistributorSalesInvoices || [];

  if (features?.foreignDistributorSales) {
    distributorSaleVehicle = [
      ...distributorSaleVehicle,
      ...(vehicle?.salesInfo?.foreignDistributorSalesInvoices?.filter(
        (item) => item.creditIndicator !== null,
      ) || []),
    ];
  }

  const isMexicanDistributor = isMexicanDistributorFn(vehicle?.orderInfo?.globalDistributorCode);

  const rows = distributorDealerSalesMapper({
    vehicle,
    documents,
    distributorSales: distributorSaleVehicle,
    roles,
    userRegion,
    isTMSDistributor: isTMSDistributorFn(vehicle?.main?.header?.distributorNameAbbrev),
    distributorInvoiceLoading,
    isMexicanDistributor,
  });
  const transactionDetails = transactionDetailsMapper(distributorSaleVehicle);

  return {
    rows,
    transactionDetails,
    heroAttributes: [
      {
        label: 'Distributor Area',
        value: formatDataValue({
          value: getLatestSalesRegionName({
            distributorDealerSales: vehicle?.salesInfo?.distributorDealerSales,
          }),
          fallbackString: '',
        }),
      },
      {
        label: 'Wholesale Count',
        value: formatDataValue({
          value: vehicle?.generalInfo?.vehicleSummary?.totalDistributorInvoiceCount,
        }),
        fallbackString: '',
      },
      {
        label: 'Credit Count',
        value: formatDataValue({
          value: vehicle?.generalInfo?.vehicleSummary?.totalDistributorCreditCount,
        }),
        fallbackString: '',
      },
    ],
  };
};
